import { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { DbtResourceIcon } from '@dbt-labs/dbt-dag';
import { CellContext, ColumnDef, DropdownOption, Tooltip } from '@dbt-labs/sourdough';
import { SortingState } from '@dbt-labs/sourdough';

import { useRecommendations } from '@/hooks/discovery/recommendations';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { ArrElement } from '@/typings/arrayElement';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { PaginatedTable } from '../PaginatedTable';
import { LinkCell } from '../Table/LinkCell';
import { formatDisplayedName } from './ruleInfo';
import { RuleSeverityCell } from './RuleSeverityCell';

import './RecommendationTable.css';

export type TableData = ArrElement<ReturnType<typeof useRecommendations>['data']>;
export type RecommendationsQuery = ReturnType<typeof useRecommendations>;
export interface RecommendationsTableProps {
  violationsQuery: RecommendationsQuery;
  pageSize: number;
  onChangeSort?: (sortArgs: SortingState) => void;
}

const PAGE_OPTIONS: DropdownOption[] = [
  { label: '5 Rows', value: 5 },
  { label: '10 Rows', value: 10 },
  { label: '25 Rows', value: 25 },
  { label: '50 Rows', value: 50 },
];

export const RecommendationsTable: FC<RecommendationsTableProps> = ({
  violationsQuery,
  pageSize,
  onChangeSort,
}) => {
  const data = violationsQuery?.data?.map((violation) => {
    return {
      ...violation,
      displayCategory: formatDisplayedName(violation.category.toLowerCase()),
      displayName: formatDisplayedName(violation.name),
      displaySeverity: formatDisplayedName(violation.severity.toLowerCase()),
      displayResourceName: parseUniqueId(violation.uniqueId).remainder,
    };
  });
  const pageInfo = violationsQuery.pageInfo;

  const pageDisplayLabel =
    data?.length == null || pageInfo?.totalCount == null
      ? undefined
      : `Loaded ${data.length} of ${pageInfo.totalCount}`;
  const resourceLink = useLinkToExplorer('resourceDetails');
  const recommendationsLink = useLinkToExplorer('recommendations');
  const projectScope = useUrlProjectParams();

  const columns: ColumnDef<TableData>[] = [
    {
      header: 'Severity',
      accessorKey: 'displaySeverity',
      id: 'severity',
      enableSorting: true,
      size: 1,
      minSize: 80,
      cell: (cellContext: CellContext<TableData, any>) => (
        <RuleSeverityCell
          {...cellContext}
          href={recommendationsLink({
            ...projectScope,
            severity: cellContext.row.original.severity as any,
          })}
        />
      ),
    },
    {
      header: 'Category',
      accessorKey: 'displayCategory',
      id: 'category',
      enableSorting: false,
      size: 1,
      minSize: 110,
    },
    {
      header: 'Rule name',
      accessorKey: 'displayName',
      id: 'rule_name',
      enableSorting: false,
      size: 2,
      minSize: 180,
      cell: (cellContext: CellContext<TableData, any>) => (
        <LinkCell
          href={recommendationsLink({
            ...projectScope,
            ruleName: cellContext.row.original.name as any,
          })}
          {...cellContext}
        />
      ),
    },
    {
      header: 'Resource',
      accessorKey: 'displayResourceName',
      id: 'resourceName',
      size: 4,
      minSize: 200,
      cell: (cellContext: CellContext<TableData, any>) => {
        const dbtUniqueId = cellContext.row.original.uniqueId;
        return (
          <div className="flex flex-row items-center space-x-1 overflow-hidden text-ellipsis">
            <DbtResourceIcon
              resource={parseUniqueId(dbtUniqueId).resourceType}
              className="text-fgMain"
            />
            <LinkCell
              href={resourceLink({
                ...projectScope,
                dbtUniqueId,
                tab: 'evaluator',
              })}
              {...cellContext}
            />
          </div>
        );
      },
      enableSorting: false,
    },
    {
      header: 'Recommendation',
      accessorKey: 'description',
      id: 'description',
      size: 10,
      maxSize: 600,
      enableSorting: false,
      cell: (cellContext: any) => {
        return (
          <Tooltip
            content={
              <ReactMarkdown
                className="prose prose-sm mt-2 w-full min-w-full text-fgDecorative dark:prose-invert"
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, [rehypeExternalLinks, { target: '_blank' }]]}
              >
                {(cellContext.row.original as TableData).description || ''}
              </ReactMarkdown>
            }
          >
            <div className="recommendation-description" style={{ maxWidth: '600px' }}>
              <ReactMarkdown
                className="prose prose-sm mt-2 w-full min-w-full text-fgDecorative dark:prose-invert"
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, [rehypeExternalLinks, { target: '_blank' }]]}
              >
                {(cellContext.row.original as TableData).description || ''}
              </ReactMarkdown>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="w-full">
      <PaginatedTable
        data={data || ([] as any[])}
        height={350}
        rowHeight={40}
        isSortable={true}
        initialSortColumn="displaySeverity"
        testId="recommendations-result"
        loadingRowCount={pageSize}
        isLoading={violationsQuery.fetching}
        maxResultCount={pageInfo?.totalCount == null ? undefined : pageInfo.totalCount}
        pageSize={pageSize}
        emptyStateProps={{
          header: `No recommendations`,
          helpText: `No recommendations were found`,
        }}
        columns={columns}
        pagination={{
          selectedPageCount: PAGE_OPTIONS.find(({ value }) => value === pageSize),
          pageOptions: PAGE_OPTIONS,
          shouldShowLoadMore: !violationsQuery.fetchMore && pageInfo?.hasNextPage,
          hasMorePages: pageInfo?.hasNextPage,
          onLoadMore: violationsQuery.fetchMore,
          isPageLoading: violationsQuery.fetching,
          pageDisplayLabel,
        }}
        onChangeSort={onChangeSort}
      />
    </div>
  );
};
